import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout, Spin, Typography, notification } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import OrganizationHeader from './components/OrganizationHeader';
import VideoUpload from './components/VideoUpload';
import JobDashboard from './components/JobDashboard';
import JobDetails from './components/JobDetails';
import Navigation from './components/Navigation';
import LanguageSwitcher from './components/LanguageSwitcher';
import MonitoringDashboard from './components/MonitoringDashboard';
import socketService from './services/socket';
import { load as loadTranslations, translations } from './utils/translations';
import { getString } from './utils/translations';
import './App.css';

const { Header, Content } = Layout;
const { Title } = Typography;

const TranslationBootstrap = () => (
  <div className="translation-bootstrap">
    <Spin
      indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
      size="large"
    />
  </div>
);

// Main App Component with Authentication
function AppContent({ locale, onLocaleChange }) {
  const [socketConnected, setSocketConnected] = useState(false);
  const { isReadOnlyMode } = useAuth();

  useEffect(() => {
    // Skip socket connection in production for now
    const API_URL = import.meta.env.VITE_API_URL;
    const isProduction = true; // Always production in built version
    
    if (isProduction) {
      // In production, skip socket connection
      setSocketConnected(false);
      return;
    }

    // Initialize socket connection (local development only)
    const initSocket = async () => {
      try {
        await socketService.connect();
        setSocketConnected(true);
        notification.success({
          message: getString('pipeline.socket.connected'),
          description: getString('pipeline.socket.real_time_enabled'),
          duration: 3,
        });
      } catch (error) {
        console.error('Failed to connect to socket:', error);
        setSocketConnected(false);
      }
    };

    initSocket();

    // Cleanup on unmount
    return () => {
      socketService.disconnect();
    };
  }, []);

  // Handle socket reconnection
  const handleReconnect = async () => {
    try {
      await socketService.reconnect();
      setSocketConnected(true);
      notification.success({
        message: getString('pipeline.socket.reconnected'),
        description: getString('pipeline.socket.real_time_restored'),
        duration: 3,
      });
    } catch (error) {
      notification.error({
        message: getString('pipeline.socket.connection_failed'),
        description: getString('pipeline.socket.unable_to_connect'),
        duration: 5,
      });
    }
  };

  return (
    <Layout className="app-layout">
      {/* Organization Context Header */}
      <OrganizationHeader />
      
      <Header className="app-header">
        <div className="header-content">
          <Title level={2} className="app-title">
            {getString('pipeline.nav.app_title', 'Video Dashboard Pro')}
          </Title>
          <Navigation 
            socketConnected={socketConnected} 
            onReconnect={handleReconnect}
          />
          <LanguageSwitcher 
            currentLocale={locale} 
            onLocaleChange={onLocaleChange} 
          />
        </div>
      </Header>
      
      <Content className="app-content">
        <div className="content-wrapper">
          <Routes>
            <Route path="/" element={<Navigate to={isReadOnlyMode ? "/jobs" : "/upload"} replace />} />
            <Route 
              path="/upload" 
              element={isReadOnlyMode
                ? <Navigate to="/jobs" replace />
                : <VideoUpload socketConnected={socketConnected} />}
            />
            <Route 
              path="/jobs" 
              element={<JobDashboard socketConnected={socketConnected} />} 
            />
            <Route 
              path="/jobs/:jobId" 
              element={<JobDetails socketConnected={socketConnected} />} 
            />
            <Route 
              path="/monitoring" 
              element={<MonitoringDashboard />} 
            />
          </Routes>
        </div>
      </Content>
    </Layout>
  );
}

// App with Authentication Provider and Protected Routes
function App() {
  const [translationsReady, setTranslationsReady] = useState(false);
  const [locale, setLocale] = useState(translations.selectedLocale || 'en');

  useEffect(() => {
    let active = true;

    const initializeTranslations = async () => {
      await loadTranslations();
      if (!active) return;

      setLocale(translations.selectedLocale || 'en');
      setTranslationsReady(true);
    };

    initializeTranslations();

    return () => {
      active = false;
    };
  }, []);

  const handleLocaleChange = (nextLocale) => {
    setLocale(nextLocale);
  };

  if (!translationsReady) {
    return <TranslationBootstrap />;
  }

  return (
    <AuthProvider>
      <ProtectedRoute>
        <AppContent locale={locale} onLocaleChange={handleLocaleChange} />
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App; 